import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const BillingContext = createContext(null);

const DEFAULT_ENT = {
  is_premium: false,
  plan: null,
  expires_at: null,
  owned_packs: [],
  premium_packs: ["after-dark", "deep-talk", "spicy-mode", "lowkey-toxic", "do-you-know-me", "confessions", "toxic-opinions"],
  free_daily_cap: 30,
};

export function BillingProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [entitlements, setEntitlements] = useState(DEFAULT_ENT);
  const [usage, setUsage] = useState({ count: 0, cap: 30, remaining: 30, is_premium: false });
  const [paywall, setPaywall] = useState(null); // { reason, packId? }
  const [unlockCelebration, setUnlockCelebration] = useState(null); // { sku }
  const [intro, setIntro] = useState(null); // { eligible, expires_at, price, sku, grants_pack }
  const [previewPack, setPreviewPack] = useState(null); // pack object for preview modal
  const [catalog, setCatalog] = useState(null); // { plans, packs, bundle, intro }

  const refreshEntitlements = useCallback(async () => {
    try {
      const e = await api.billingEntitlements();
      setEntitlements(e);
    } catch {
      setEntitlements(DEFAULT_ENT);
    }
  }, []);

  const refreshUsage = useCallback(async () => {
    try {
      setUsage(await api.usageToday());
    } catch { /* ignore */ }
  }, []);

  const refreshCatalog = useCallback(async () => {
    try {
      setCatalog(await api.billingCatalog());
    } catch { /* ignore */ }
  }, []);

  const startOrFetchIntro = useCallback(async () => {
    try {
      const i = await api.billingIntroStatus(true);
      setIntro(i);
      return i;
    } catch {
      return null;
    }
  }, []);

  const fetchIntroReadOnly = useCallback(async () => {
    try {
      const i = await api.billingIntroStatus(false);
      setIntro(i);
      return i;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    refreshEntitlements();
    refreshUsage();
    refreshCatalog();
    startOrFetchIntro();
  }, [authLoading, user, refreshEntitlements, refreshUsage, refreshCatalog, startOrFetchIntro]);

  const isPackUnlocked = useCallback(
    (packId) => {
      if (!entitlements.premium_packs.includes(packId)) return true;
      if (entitlements.is_premium) return true;
      return (entitlements.owned_packs || []).includes(packId);
    },
    [entitlements]
  );

  const value = {
    entitlements,
    usage,
    catalog,
    isPremium: entitlements.is_premium,
    isPackUnlocked,
    paywall,
    openPaywall: (reason, packId) => {
      setPaywall({ reason, packId });
      // Start / extend the intro timer when the user opens a paywall.
      startOrFetchIntro();
    },
    closePaywall: () => setPaywall(null),
    unlockCelebration,
    triggerUnlockCelebration: (sku) => setUnlockCelebration({ sku }),
    clearUnlockCelebration: () => setUnlockCelebration(null),
    refreshEntitlements,
    refreshUsage,
    intro,
    startOrFetchIntro,
    fetchIntroReadOnly,
    previewPack,
    openPackPreview: (pack) => setPreviewPack(pack),
    closePackPreview: () => setPreviewPack(null),
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used within BillingProvider");
  return ctx;
}
